import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid2,
  TextField,
  Typography,
  Autocomplete,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useApartmentListForCourse } from "../../../assets/api/useGetApi";
import {
  postLogin,
  LOGIN_ROUTE_BY_ROLE,
} from "../../../assets/nodeApi/postApi/postSchoolApi";
import SkillVationAuthLayout from "../../../layout/SkillVationAuthLayout";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useMutation } from "@tanstack/react-query";
import useAuthStore from "../../../shared/store/useAuthStore";
import { useToaster } from "../../../shared/utils/ToasterProvider";
import CloseIcon from "@mui/icons-material/Close";
import useOtpVerification from "./useOtpVerification"; 
import OtpModal from "./OtpModal";                

const textFieldStyle = {
  "& .MuiOutlinedInput-root": {
    height: 48,
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    "& fieldset": { borderColor: "#9D9D9D", borderWidth: "1px" },
    "&:hover fieldset": { borderColor: "#9D9D9D" },
    "&.Mui-focused": { backgroundColor: "#FFFFFF" },
    "&.Mui-focused fieldset": { borderColor: "#4E93E1", borderWidth: "2px" },
    "&.Mui-disabled": { backgroundColor: "#F5F5F5" },
    "&.Mui-disabled fieldset": { borderColor: "#9D9D9D" },
  },
  "& .MuiInputBase-input": { padding: "12px 14px", fontSize: "16px", fontWeight: 400 },
};

const submitButtonStyle = {
  height: 48,
  borderRadius: "8px",
  backgroundColor: "#4E93E1",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "16px",
  boxShadow: "none",
  color: "#fff",
  "&:hover": { backgroundColor: "#2563EB", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#4E93E1", opacity: 0.6, color: "#fff" },
};

const errorTextStyle = { fontSize: "10px", color: "#DC143C", fontWeight: 400 };

const StudentRegisterForm = ({ signupRoute, bottomLine }) => {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const { showToaster } = useToaster();

  // ── Apartment state ──────────────────────────────────────────────────
  const [selectedApartment,    setSelectedApartment]    = useState(null);
  const [isStayingInApartment, setIsStayingInApartment] = useState(true);
  const [showStayConfirm,      setShowStayConfirm]      = useState(true);
  const [showAddApartment,     setShowAddApartment]     = useState(false);

  // Add-apartment temp fields (kept outside RHF to avoid conflicts)
  const [newApartmentName,         setNewApartmentName]         = useState("");
  const [newApartmentAddress,      setNewApartmentAddress]      = useState("");
  const [newApartmentNameError,    setNewApartmentNameError]    = useState("");
  const [newApartmentAddressError, setNewApartmentAddressError] = useState("");

  // ── Password visibility ──────────────────────────────────────────────
  const [showPassword,        setShowPassword]        = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ── Server error ─────────────────────────────────────────────────────
  const [errorText, setErrorText] = useState("");

  // ── Apartment options ────────────────────────────────────────────────
  const { data: apartmentList, isLoading: isLoadingApartment } = useApartmentListForCourse();

  const apartmentOptions = (apartmentList?.data || [])
    .filter((item) => {
      const status = item?.terms_status || "";
      return (
        status.includes("Approved By") ||
        ["Accepted By Apartment", "Created"].includes(status)
      );
    })
    .map((item) => ({
      id:      item?.id,
      label:   item?.apartment_name,
      value:   item?.apartment_name,
      address: item?.address || item?.apartment_name || "",
    }));

  // ── React Hook Form ──────────────────────────────────────────────────
  const {
    register,
    handleSubmit,
    watch,
    setError,
    setValue,
    clearErrors,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      firstname:    "",
      lastname:     "",
      email:        "",
      username:     "",
      mobile:       "",
      password:     "",
      confirm_password: "",
      apartment_id: "",
      address:      "",
    },
  });

  // ── OTP hook — replaces 8 useState + timerRef + 3 handler functions ──
  const otpHook = useOtpVerification({
    role: "user",
    onSendSuccess:   () => showToaster("Success", "OTP sent successfully!", "success"),
    onVerified:      () => {
      clearErrors("mobile");
      showToaster("Success", "OTP verified successfully!", "success");
    },
    onResendSuccess: () => showToaster("Success", "OTP resent successfully!", "success"),
    onError:         (msg) => showToaster("Error", msg, "error"),
  });

  // ── Signup mutation ──────────────────────────────────────────────────
  const signupMutation = useMutation({
    mutationFn: (formData) => postLogin(signupRoute, formData),
  });

  // ── Add apartment ────────────────────────────────────────────────────
  const closeAddApartmentDialog = () => {
    setShowAddApartment(false);
    setNewApartmentName("");
    setNewApartmentAddress("");
    setNewApartmentNameError("");
    setNewApartmentAddressError("");
  };

  const handleAddApartment = () => {
    if (!newApartmentName) {
      setNewApartmentNameError("Apartment name is required");
      return;
    }
    if (!newApartmentAddress) {
      setNewApartmentAddressError("Address is required");
      return;
    }

    const newApartment = {
      id:      `new_${Date.now()}`,
      label:   newApartmentName,
      value:   newApartmentName,
      address: newApartmentAddress,
    };

    setSelectedApartment(newApartment);
    setValue("apartment_id", newApartment.id);
    setValue("address", newApartmentAddress);
    clearErrors("apartment_id");
    closeAddApartmentDialog();
    showToaster("Success", "Apartment added successfully", "success");
  };

  // ── Apartment selection ──────────────────────────────────────────────
  const handleApartmentChange = (_, newValue) => {
    setSelectedApartment(newValue);
    setValue("apartment_id", newValue?.id || "");
    setValue("address", newValue?.address || newValue?.label || "");
    if (newValue) clearErrors("apartment_id");
  };

  // ── Submit ───────────────────────────────────────────────────────────
  const handleRegister = async (values) => {
    setErrorText("");

    if (!otpHook.isVerified) {
      setError("mobile", { message: "Please verify your mobile number first" });
      return;
    }
    if (values.password !== values.confirm_password) {
      setError("confirm_password", { message: "Passwords do not match" });
      return;
    }
    if (isStayingInApartment && !selectedApartment) {
      setError("apartment_id", { message: "Please select an apartment" });
      return;
    }

    try {
      let mobile = values.mobile?.trim().replace(/\D/g, "");
      if (mobile.length > 10) mobile = mobile.slice(-10);

      const payload = {
        firstname: values.firstname?.trim(),
        lastname:  values.lastname?.trim(),
        email:     values.email?.trim(),
        username:  values.username?.trim(),
        password:  values.password,
        mobile,
        ...(isStayingInApartment && selectedApartment
          ? {
              apartment_name: selectedApartment.label || selectedApartment.value,
              address: selectedApartment?.address || selectedApartment?.label || "",
            }
          : { apartment_name: "Online" }),
      };

      const signUpResponse = await signupMutation.mutateAsync(payload);

      if (signUpResponse?.data?.user) {
        showToaster("Success", "Registered successfully! Logging you in…", "success");

        login(signUpResponse.data.user, true);

        const storedPath = localStorage.getItem("lastVisitedPath") || "/";
        navigate(storedPath, { replace: true });
        setTimeout(() => localStorage.removeItem("lastVisitedPath"), 500);

        reset();
        otpHook.resetOtp();        // ← replaces manual setIsVerified(false)
        setSelectedApartment(null);
      } else {
        showToaster("Error", "Something went wrong during sign-up", "error");
      }
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Registration failed. Please try again.";
      setErrorText(msg);
      showToaster("Error", msg, "error");
    }
  };

  return (
    <>
      {/* ── Stay Confirmation Dialog ───────────────────────────────────── */}
      <Dialog
        open={showStayConfirm}
        onClose={() => setShowStayConfirm(false)}
        PaperProps={{ sx: { borderRadius: "16px", maxWidth: 420, p: 1 } }}
      >
        <DialogContent>
          <Box textAlign="center" mb={3}>
            <Typography variant="h5" fontWeight={700} color="text.primary" fontSize="20px">
              Apartment Stay Confirmation
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1} fontSize="14px" fontWeight={400}>
              Before you proceed, please confirm if you are currently staying in an apartment
            </Typography>
          </Box>

          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => { setIsStayingInApartment(true); setShowStayConfirm(false); }}
                sx={{
                  height: 48, borderRadius: "8px", borderColor: "#4E93E1",
                  color: "#4E93E1", textTransform: "none", fontWeight: 500, fontSize: "14px",
                  "&:hover": { borderColor: "#2563EB", backgroundColor: "rgba(78,147,225,0.04)" },
                }}
              >
                Yes, I am staying
              </Button>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => { setIsStayingInApartment(false); setShowStayConfirm(false); }}
                sx={{ ...submitButtonStyle, fontWeight: 400, fontSize: "14px" }}
              >
                No, I am not staying
              </Button>
            </Grid2>
          </Grid2>
        </DialogContent>
      </Dialog>

      {/* ── Add Apartment Dialog ───────────────────────────────────────── */}
      <Dialog
        open={showAddApartment}
        onClose={closeAddApartmentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography variant="h6" fontWeight={600} fontSize="18px">
            Add New Apartment
          </Typography>
          <IconButton onClick={closeAddApartmentDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2.5} pt={1}>
            <Box>
              <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
                Apartment Name <span style={{ color: "#ef4444" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter apartment name"
                value={newApartmentName}
                onChange={(e) => { setNewApartmentName(e.target.value); if (e.target.value) setNewApartmentNameError(""); }}
                error={!!newApartmentNameError}
                helperText={newApartmentNameError}
                sx={textFieldStyle}
                InputProps={{ sx: { fontSize: "16px", fontWeight: 400 } }}
              />
            </Box>

            <Box>
              <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
                Address <span style={{ color: "#ef4444" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter apartment address"
                multiline
                rows={2}
                value={newApartmentAddress}
                onChange={(e) => { setNewApartmentAddress(e.target.value); if (e.target.value) setNewApartmentAddressError(""); }}
                error={!!newApartmentAddressError}
                helperText={newApartmentAddressError}
                sx={{ ...textFieldStyle, "& .MuiOutlinedInput-root": { ...textFieldStyle["& .MuiOutlinedInput-root"], height: "auto" } }}
                InputProps={{ sx: { fontSize: "16px", fontWeight: 400 } }}
              />
            </Box>

            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Button fullWidth variant="outlined" onClick={closeAddApartmentDialog}
                  sx={{ height: 48, borderRadius: "8px", textTransform: "none", fontWeight: 500 }}>
                  Cancel
                </Button>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Button fullWidth variant="contained" onClick={handleAddApartment} sx={submitButtonStyle}>
                  Add Apartment
                </Button>
              </Grid2>
            </Grid2>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Main Form ─────────────────────────────────────────────────── */}
      <SkillVationAuthLayout
        title="Welcome!!"
        subtitle="Student Signup to join courses and events in your community"
        bottomText="Already have an account?"
        bottomLinkLabel="Sign In"
        bottomLinkTo="/login"
      >
        <Box
          component="form"
          onSubmit={handleSubmit(handleRegister)}
          display="flex"
          flexDirection="column"
          gap={2.5}
        >
          {/* Row 1 — Parent & Student Name */}
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
                Parent's Name <span style={{ color: "#ef4444" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter parent's name"
                error={!!errors.firstname}
                helperText={errors.firstname?.message}
                {...register("firstname", { required: "Parent's name is required" })}
                sx={textFieldStyle}
                InputProps={{ sx: { fontSize: "16px", fontWeight: 400 } }}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
                Student's Name <span style={{ color: "#ef4444" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter student's name"
                error={!!errors.lastname}
                helperText={errors.lastname?.message}
                {...register("lastname", { required: "Student's name is required" })}
                sx={textFieldStyle}
                InputProps={{ sx: { fontSize: "16px", fontWeight: 400 } }}
              />
            </Grid2>
          </Grid2>

          {/* Row 2 — Email & Username */}
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
                E-Mail <span style={{ color: "#ef4444" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                type="email"
                placeholder="Enter your email"
                error={!!errors.email}
                helperText={errors.email?.message}
                {...register("email", {
                  required: "Email is required",
                  pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Invalid email address" },
                })}
                sx={textFieldStyle}
                InputProps={{ sx: { fontSize: "16px", fontWeight: 400 } }}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
                User Name <span style={{ color: "#ef4444" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter your username"
                error={!!errors.username}
                helperText={errors.username?.message}
                {...register("username", { required: "Username is required" })}
                sx={textFieldStyle}
                InputProps={{ sx: { fontSize: "16px", fontWeight: 400 } }}
              />
            </Grid2>
          </Grid2>

          {/* Apartment — only if staying */}
          {isStayingInApartment && (
            <Box>
              <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
                Apartment <span style={{ color: "#ef4444" }}>*</span>
              </Typography>

              <input type="hidden" {...register("apartment_id")} />

              <Autocomplete
                options={apartmentOptions}
                getOptionLabel={(option) => option.label || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={isLoadingApartment}
                value={selectedApartment}
                onChange={handleApartmentChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select your apartment"
                    error={!!errors.apartment_id}
                    helperText={errors.apartment_id?.message}
                    sx={textFieldStyle}
                  />
                )}
              />

              <Box display="flex" justifyContent="flex-end" mt={1}>
                <Typography
                  variant="body2"
                  color="#4E93E1"
                  fontSize="12px"
                  fontWeight={400}
                  onClick={() => setShowAddApartment(true)}
                  sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                >
                  Didn't find your apartment?
                </Typography>
              </Box>

              {selectedApartment && (
                <Box mt={2}>
                  <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
                    Address
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Address will be auto-filled"
                    value={selectedApartment?.address || selectedApartment?.label || ""}
                    disabled
                    sx={{ ...textFieldStyle, "& .MuiInputBase-input": { ...textFieldStyle["& .MuiInputBase-input"], color: "#6B7280" } }}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Mobile with Verify */}
          <Box>
            <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
              Mobile Number <span style={{ color: "#ef4444" }}>*</span>
            </Typography>
            <Box display="flex" gap={1}>
              <TextField
                fullWidth
                type="tel"
                placeholder="Enter mobile number"
                error={!!errors.mobile}
                helperText={errors.mobile?.message}
                disabled={otpHook.isVerified}
                {...register("mobile", {
                  required: "Mobile number is required",
                  pattern: { value: /^[0-9]{10}$/, message: "Enter a valid 10-digit mobile number" },
                })}
                sx={textFieldStyle}
                InputProps={{ sx: { fontSize: "16px", fontWeight: 400 } }}
              />
              <Button
                type="button"
                variant="outlined"
                onClick={() => otpHook.sendOtp(watch("mobile"), setError)}
                disabled={otpHook.isVerified || otpHook.isSendPending}
                sx={{
                  height: 48, px: 3, borderRadius: "8px", textTransform: "none",
                  fontWeight: 500, fontSize: "14px", whiteSpace: "nowrap",
                  flexShrink: 0, alignSelf: "flex-start",
                  ...(otpHook.isVerified
                    ? {
                        borderColor: "#22c55e", color: "#16a34a", backgroundColor: "#f0fdf4",
                        "&:hover": { backgroundColor: "#f0fdf4" },
                        "&.Mui-disabled": { borderColor: "#22c55e", color: "#16a34a", backgroundColor: "#f0fdf4" },
                      }
                    : {
                        borderColor: "#4E93E1", color: "#4E93E1",
                        "&:hover": { backgroundColor: "#eff6ff", borderColor: "#4E93E1" },
                      }),
                }}
              >
                {otpHook.isVerified ? "✓ Verified" : otpHook.isSendPending ? "Sending…" : "Verify"}
              </Button>
            </Box>
          </Box>

          {/* Server error */}
          {errorText && (
            <Typography variant="body2" sx={errorTextStyle}>
              {errorText}
            </Typography>
          )}

          {/* Password fields — shown only after OTP verified */}
          {otpHook.isVerified && (
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
                  Password <span style={{ color: "#ef4444" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password"
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  {...register("password", {
                    required: "Password is required",
                    minLength: { value: 8, message: "At least 8 characters" },
                  })}
                  sx={textFieldStyle}
                  InputProps={{
                    sx: { fontSize: "16px", fontWeight: 400 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((p) => !p)} edge="end"
                          sx={{ color: "#9D9D9D", "&:hover": { color: "#4E93E1" } }}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
                  Confirm Password <span style={{ color: "#ef4444" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  error={!!errors.confirm_password}
                  helperText={errors.confirm_password?.message}
                  {...register("confirm_password", {
                    required: "Please confirm your password",
                    validate: (val) => val === watch("password") || "Passwords do not match",
                  })}
                  sx={textFieldStyle}
                  InputProps={{
                    sx: { fontSize: "16px", fontWeight: 400 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword((p) => !p)} edge="end"
                          sx={{ color: "#9D9D9D", "&:hover": { color: "#4E93E1" } }}>
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid2>
            </Grid2>
          )}

          {/* Submit */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isSubmitting}
            sx={submitButtonStyle}
          >
            {isSubmitting ? "Signing Up…" : "Sign Up"}
          </Button>

          <Grid2 size={12}>{bottomLine}</Grid2>
        </Box>
      </SkillVationAuthLayout>

      {/* OTP Modal — replaces the inline <Dialog> OTP block */}
      <OtpModal
        mobile={watch("mobile")}
        open={otpHook.showOtpModal}
        onClose={otpHook.closeModal}
        otp={otpHook.otp}
        setOtp={otpHook.setOtp}
        otpError={otpHook.otpError}
        isVerifyPending={otpHook.isVerifyPending}
        isResendPending={otpHook.isResendPending}
        canResend={otpHook.canResend}
        countdown={otpHook.countdown}
        verifyOtp={otpHook.verifyOtp}
        resendOtp={otpHook.resendOtp}
      />
    </>
  );
};

export default StudentRegisterForm;